// Generated by tutorials/record.cjs from tutorials/articles/h28-tray-line-setup.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h28 --text-only` (wording) or
// `node tutorials/record.cjs h28` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'tray-line-setup',
  catalogId: 'h28',
  title: 'Setting up the tray line',
  summary: 'Put rooms and beds in the order your tray line runs, cart by cart, so tray tickets print in the order trays are made and delivered.',
  audience: 'Dietary managers and kitchen leads who run the tray line',
  area: 'kitchen',
  learn: [
    'What a set and a tray number are',
    'See a cart\'s rooms in tray order for a meal',
    'Add a room that is not on the tray line',
    'Check each resident\'s place on the Resident Assignment tab',
  ],
  keywords: [
    'tray line',
    'trayline',
    'cart',
    'set',
    'tray',
    'tray order',
    'room order',
    'room bed',
    'tray line setup',
    'auto assign',
    'print order',
    'delivery order',
  ],
  steps: [
    {
      id: 'open',
      part: 'Rooms in tray order',
      heading: 'Open Tray Line Setup',
      text: 'In the menu, select Kitchen, then Tray Line Setup. On Room/Bed Setup, each set is a cart or a group of trays. The tray number is the room\'s place in that set: 1 is made and delivered first.',
      media: {
        image: {
          src: '/help/tray-line-setup/open.webp',
          width: 1280,
          height: 936,
        },
        alt: 'Tray Line Setup on the Room/Bed Setup tab, with Menu Time, Set No. and Unassigned Unit at the top, and the Assigned and Unassigned lists.',
      },
    },
    {
      id: 'pick-set',
      part: 'Rooms in tray order',
      heading: 'Pick the meal and the set',
      text: 'Pick the meal under Menu Time, then the set under Set No. Assigned lists that set\'s rooms and beds in tray order.',
      media: {
        image: {
          src: '/help/tray-line-setup/pick-set.webp',
          width: 1280,
          height: 936,
        },
        clip: {
          src: '/help/tray-line-setup/pick-set.mp4',
          width: 1280,
          height: 936,
          durationSec: 4,
        },
        alt: 'Breakfast and Maple Cart 1 picked, with rooms 101 A to 111 B of Maple listed in tray order 1 to 10.',
      },
    },
    {
      id: 'unassigned',
      part: 'Rooms in tray order',
      heading: 'Find the rooms not on the tray line',
      text: 'Pick a unit under Unassigned Unit. Unassigned lists that unit\'s rooms and beds that are not in any set for this meal.\n\nA resident in one of these rooms gets no set or tray number on their ticket, and shows No Tray Line on the Residents page.',
      media: {
        image: {
          src: '/help/tray-line-setup/unassigned.webp',
          width: 1280,
          height: 936,
        },
        clip: {
          src: '/help/tray-line-setup/unassigned.mp4',
          width: 1280,
          height: 936,
          durationSec: 4.2,
        },
        alt: 'Unassigned Unit Maple picked, with rooms 109 A and 109 B in the Unassigned list.',
      },
    },
    {
      id: 'assign',
      part: 'Rooms in tray order',
      heading: 'Add a room to the set',
      text: 'Double-click a room in Unassigned. It is added at the end of the set.\n\nTo move it to its place, drag it up the Assigned list, or select it and use the up and down arrows at the top right of the list.',
      media: {
        image: {
          src: '/help/tray-line-setup/assign.webp',
          width: 1280,
          height: 936,
        },
        alt: 'Room 109 A now at the end of the Assigned list for Maple Cart 1, with tray number 11.',
      },
    },
    {
      id: 'save',
      part: 'Rooms in tray order',
      heading: 'Save',
      text: 'Select Save. Nothing changes for the kitchen until you save. The bottom of the page says when there are unsaved changes.',
      media: {
        image: {
          src: '/help/tray-line-setup/save.webp',
          width: 1280,
          height: 936,
        },
        clip: {
          src: '/help/tray-line-setup/save.mp4',
          width: 1280,
          height: 936,
          durationSec: 3.9,
        },
        alt: 'Tray Line Setup after saving, with 11 rooms assigned to Maple Cart 1 for breakfast.',
      },
    },
    {
      id: 'residents',
      part: 'Residents on the tray line',
      heading: 'Check each resident\'s place',
      text: 'Select Resident Assignment. It lists the residents in the set with their tray number for the meal, and below them the residents who are not on the tray line yet.\n\nDouble-click a resident under Unassigned Residents to add them to the set, then select Save.',
      media: {
        image: {
          src: '/help/tray-line-setup/residents.webp',
          width: 1280,
          height: 936,
        },
        clip: {
          src: '/help/tray-line-setup/residents.mp4',
          width: 1280,
          height: 936,
          durationSec: 3.6,
        },
        alt: 'The Resident Assignment tab: ten residents of Maple Cart 1 with their trays for breakfast, and three unassigned residents below.',
      },
    },
  ],
  tips: [
    'Set up breakfast first, then pick Lunch and Dinner under Menu Time and check them. Each meal has its own order, because some facilities run their carts differently at each meal.',
    'Auto Assign adds every unassigned room of the picked unit to the end of the cart, in room and bed order. It is the quickest way to set up a new unit.',
    'Print tray tickets with Order By set to Tray Line to get them in this order.',
  ],
  notes: [
    'The tray line follows the room and bed. A resident who moves to another room takes that room\'s place on the line.',
    'Only one person can change the tray line at a time. If someone else has it open, you see it read only; select Refresh to try again.',
    'Changing the tray line needs the right role. If Save is missing, ask your administrator.',
  ],
  related: ['tray-tickets', 'admit-resident', 'discharge-readmit'],
  recorded: '2026-10-25',
};

export default article;
